import { useState } from 'react';
import { Dropdown, message } from 'antd';
import { CheckOutlined, ClusterOutlined, DownOutlined, LinkOutlined, MailOutlined, PlusOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import NodeLogo from './NodeLogo';
import JoinNodeModal from './JoinNodeModal';
import { useMyNodes } from '../../hooks/use_my_nodes';
import { useMyNodeInvites } from '../../hooks/use_node_invites';
import { useNodeInfo } from '../../hooks/use_node_info';

/**
 * The nav bar's logo, and behind it the nodes this person belongs to
 * (GET /api/nodes/mine): the current one, the others — each on its own
 * address, so switching is a full navigation — then the nodes they have been
 * invited to (GET /api/node-invites/mine, "Join Acme"), "Join a node…" for an
 * invite link that reached them some other way, and, on a platform node with
 * self-service on, "New node". Someone with one node, no invites and nowhere
 * to create another sees the plain logo, which goes home as it always has.
 * `label` (the nav passes the node's name) sits beside the logo, inside the
 * same click target.
 */
export function NodeSwitcher({ size = 20, className, onHome, label }) {
    const navigate = useNavigate();
    const { nodes } = useMyNodes();
    const { invites, accept } = useMyNodeInvites();
    const { featureSelfServiceNodes, nodeIsPlatform } = useNodeInfo();
    const [joinOpen, setJoinOpen] = useState(false);
    const canCreate = featureSelfServiceNodes && nodeIsPlatform;

    if (nodes.length <= 1 && !canCreate && invites.length === 0) {
        if (!label) return <NodeLogo size={size} className={className} onClick={onHome} />;
        return (
            <span
                className={className}
                role="button"
                onClick={onHome}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}
            >
                <NodeLogo size={size} />
                {label}
            </span>
        );
    }

    // Where an accepted invite leads: the node's own address, or home when
    // it is this very node (joining it approved a pending registration).
    const open = joined => {
        if (joined?.is_current) onHome?.();
        else if (joined?.url) window.location.assign(joined.url);
    };
    const join = token => accept(token).then(open).catch(err => message.error(err?.message || 'Could not join that node'));

    const items = nodes.map(node => ({
        key: node.slug,
        label: node.name,
        icon: node.is_current ? <CheckOutlined /> : <ClusterOutlined />,
        onClick: () => {
            if (node.is_current) onHome?.();
            else if (node.url) window.location.assign(node.url);
        },
    }));
    if (invites.length) {
        if (items.length) items.push({ type: 'divider' });
        invites.forEach(invite => items.push({
            key: `invite-${invite.token}`,
            label: `Join ${invite.node_name}`,
            icon: <MailOutlined />,
            onClick: () => join(invite.token),
        }));
    }
    if (items.length) items.push({ type: 'divider' });
    items.push({ key: 'join-node', label: 'Join a node…', icon: <LinkOutlined />, onClick: () => setJoinOpen(true) });
    if (canCreate) {
        items.push({ key: 'new-node', label: 'New node…', icon: <PlusOutlined />, onClick: () => navigate('/nodes/new') });
    }

    return (
        <>
            <Dropdown menu={{ items }} trigger={['click']} placement="bottomLeft">
                <span
                    className={className}
                    role="button"
                    aria-label="Switch node"
                    style={{ display: 'inline-flex', alignItems: 'center', gap: label ? 8 : 4, cursor: 'pointer' }}
                >
                    <NodeLogo size={size} />
                    {label}
                    <DownOutlined style={{ fontSize: 10, color: 'var(--text-secondary)' }} />
                </span>
            </Dropdown>
            <JoinNodeModal
                open={joinOpen}
                onClose={() => setJoinOpen(false)}
                invites={invites}
                accept={accept}
                onJoined={joined => { setJoinOpen(false); open(joined); }}
            />
        </>
    );
}

export default NodeSwitcher;
